import { router } from "@inertiajs/react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    CircularProgress,
    Grid,
    Stack,
    Typography,
} from "@mui/material";
import ZoomButton from "app/frontend/components/buttons/ZoomButton";
import { useIsEligibilityReadOnly } from "app/frontend/components/contact/useIsEligibilityReadOnly";
import { EEligibilityStep } from "app/frontend/components/eligibility/constants";
import { useEdit } from "app/frontend/components/eligibility/hooks/useEdit";
import { useHead } from "app/frontend/components/eligibility/hooks/useHead";
import LinkButton from "app/frontend/components/LinkButton";
import { ROUTES } from "app/frontend/constants/routes";
import { useParams } from "app/frontend/hooks/useParams";
import { PropsWithChildren, useCallback, useMemo, useState } from "react";

interface IProps extends PropsWithChildren {
    header: string;
    h2: React.ReactNode | string;
    h4?: string;
    onContinue?: () => void;
    isContinueDisabled?: boolean;
    hideBackButton?: boolean;
    step: EEligibilityStep;
}

const BaseEligibilityAccordion: React.FC<IProps> = ({
    header,
    h2,
    h4,
    onContinue,
    hideBackButton = false,
    isContinueDisabled = false,
    step,
    children,
}) => {
    const readOnly = useIsEligibilityReadOnly();

    const params = useParams();
    const head = useHead();
    const edit = useEdit();

    const [isLoading, setLoading] = useState<boolean>(false);
    router.on("start", () => setLoading(true));
    router.on("finish", () => setLoading(false));

    const handleEdit = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();

            router.get(
                ROUTES.Eligibility.ROOT,
                { ...params, edit: edit === step ? 0 : step },
                { preserveState: true, preserveScroll: true },
            );
        },
        [edit, params, step],
    );

    const handleBack = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();

            const newEdit = edit ? edit - 1 : head - 1;
            if (newEdit < 0) {
                return;
            }

            router.get(
                ROUTES.Eligibility.ROOT,
                { ...params, edit: newEdit },
                { preserveState: true, preserveScroll: true },
            );
        },
        [edit, head, params],
    );

    const handleStartOverEligibility = useCallback(() => {
        router.delete(ROUTES.Eligibility.RESET);
    }, []);

    const expanded = useMemo(() => {
        if (edit) {
            return step === edit;
        } else {
            return step === head;
        }
    }, [head, edit, step]);

    return (
        <Accordion
            sx={{
                width: "100%",
                boxShadow: "none",
                "&::before": {
                    backgroundColor: "transparent",
                },
            }}
            expanded={expanded}
            slotProps={{
                heading: {
                    style: {
                        display: header && !expanded ? undefined : "none",
                    },
                    component: "div",
                },
            }}
        >
            {head >= step && (
                <AccordionSummary
                    component="div"
                    aria-controls={`base-eligibility-content-${step}`}
                    id={`base-eligibility-header-${step}`}
                    sx={{
                        padding: {
                            lg: 0,
                        },
                    }}
                >
                    <Stack
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        sx={{
                            borderBottom: "2px solid",
                            borderColor: "secondary.light",
                            width: "100%",
                            paddingBottom: 2,
                            paddingX: {
                                xs: 2,
                                lg: "unset",
                            },
                            paddingTop: {
                                xs: 0,
                                lg: "unset",
                            },
                        }}
                    >
                        <Box sx={{ display: { xs: "none", md: "block" } }}>&nbsp;</Box>
                        <Box
                            sx={{
                                fontWeight: 800,
                                fontSize: "23px",
                                lineHeight: "30px",
                                color: "secondary.main",
                                textTransform: "capitalize",
                                "&:hover": {
                                    color: "secondary.dark",
                                },
                            }}
                        >
                            {header}
                        </Box>
                        <Box>
                            {step === EEligibilityStep.PROJECT_TYPE ? (
                                <LinkButton
                                    href="#"
                                    skipRouting
                                    onClick={handleStartOverEligibility}
                                    button={{
                                        sx: {
                                            mr: 1,
                                            textAlign: "right",
                                        },
                                    }}
                                >
                                    Restart
                                </LinkButton>
                            ) : (
                                <LinkButton
                                    href="#"
                                    skipRouting
                                    onClick={handleEdit}
                                    button={{
                                        sx: {
                                            mr: 1,
                                            textAlign: "right",
                                        },
                                    }}
                                >
                                    Edit
                                </LinkButton>
                            )}
                        </Box>
                    </Stack>
                </AccordionSummary>
            )}
            <AccordionDetails>
                <Typography
                    variant="h2"
                    component={"h2"}
                    textAlign={"center"}
                    textTransform={"capitalize"}
                    mb={3}
                    sx={{ pt: { xs: step === EEligibilityStep.PROJECT_TYPE ? 3 : undefined, md: 0 } }}
                >
                    {h2}
                </Typography>
                {!!h4 && (
                    <Typography
                        variant="h4"
                        component={"h3"}
                        textAlign={"center"}
                        sx={(t) => ({
                            [t.breakpoints.down("sm")]: {
                                fontWeight: 400,
                                fontSize: "16px",
                                lineHeight: "24px",
                            },
                        })}
                    >
                        {h4}
                    </Typography>
                )}

                {children}

                {!readOnly && onContinue && (
                    <Grid size={12} sx={{ mt: 2 }}>
                        <Stack direction={"column"}>
                            <Stack
                                direction={{ xs: "column-reverse", md: "row" }}
                                gap={2}
                                alignItems={"center"}
                                justifyContent={"center"}
                                marginBottom={{ xs: 2, md: "unset" }}
                            >
                                {!hideBackButton && (
                                    <LinkButton
                                        href="#"
                                        skipRouting
                                        button={{
                                            disabled: isLoading,
                                            sx: {
                                                width: {
                                                    xs: "100%",
                                                    md: "unset",
                                                },
                                            },
                                        }}
                                        onClick={handleBack}
                                    >
                                        Back
                                    </LinkButton>
                                )}
                                <ZoomButton
                                    LinkComponent={"button"}
                                    variant="contained"
                                    href="#"
                                    id="eligibility-continue-button"
                                    disabled={isLoading || isContinueDisabled}
                                    sx={{
                                        borderRadius: 10,
                                        boxShadow: 0,
                                        margin: 2,
                                        padding: "16px 24px",
                                        fontWeight: "bold",
                                        fontSize: "14px",
                                        width: {
                                            xs: "100%",
                                            sm: "70%",
                                            md: "unset",
                                        },
                                    }}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onContinue();
                                    }}
                                >
                                    Continue
                                    <span style={{ display: isLoading ? "inline-block" : "none" }}>
                                        <CircularProgress
                                            size="1.5em"
                                            sx={{ ml: 1, color: "white", verticalAlign: "middle" }}
                                        />
                                    </span>
                                </ZoomButton>
                            </Stack>
                        </Stack>
                    </Grid>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

export default BaseEligibilityAccordion;
